import { HcCallType, HcFlow } from "./hc-flow.model";
import { HcLovedOne } from "./hc-loved-one.model";
import { HcContact } from "./hc-contact.model";
import { HcCommunicationMethodType } from "./hc-notification-setup.model";
import { HcResponseMeaning } from "./hc-response-meaning.enum";
import { HcResponseAction } from "./hc-response-actions.enum";
import { HcCallSource } from "./hc-call-source.enum";

export interface HcHistory {
  _id: string;
  flowId: string; // Reference to HcFlow._id
  readonly flow?: HcFlow; // Populated on queries via aggregation
  csId: string; // Care seeker ID
  lovedOneId: string; // Reference to HcLovedOne._id
  readonly lovedOne?: HcLovedOne; // Populated on queries via aggregation

  // Call execution details
  scheduledAt: Date; // When the call was scheduled to be made
  initiatedAt: Date; // When the call was actually initiated
  completedAt?: Date; // When the call was completed (if successful)
  timeoutAt?: Date; // When the call timed out (if applicable)

  // Call configuration snapshot (at time of call)
  callType: HcCallType;
  phoneNumber: string; // Phone number called
  timezone: string; // Timezone used for scheduling
  callSource?: HcCallSource; // Origin of the call (test-call vs scheduled)

  // Response tracking
  responseType: HcResponseType;
  responseData?: HcResponseData;
  responseMeaning?: HcResponseMeaning;
  /** When NEED_HELP: 'no_response' = no answer during call (possible incapacitation); 'explicit' or unset = user indicated need help */
  escalationReason?: 'explicit' | 'no_response';

  // Actions taken
  actionsTriggered: HcResponseAction[];
  notificationsSent: HcNotification[];
  notificationAttempts: string[] | HcNotificationAttempt[]; // Stored as string[] IDs, populated as HcNotificationAttempt[] on queries

  // Notification flow tracking
  notificationFlowState?: HcNotificationFlowState; // Current state of notification flow
  notificationFlowCompleted?: boolean;
  notificationFlowCompletedAt?: Date;
  notificationFlowInitiatedAt?: Date; // When notification flow started
  acknowledgmentDeadline?: Date; // When acknowledgment is due for NOT_OK scenarios
  lastNotificationSentForDeadline?: Date; // When notifications were last sent for the current acknowledgment deadline (prevents duplicate processing)

  // Call status and metadata
  status: HcCallStatus;
  duration?: number; // Call duration in seconds
  retryAttempt: number; // Which retry attempt this was (0 = first attempt)
  /** When the next attempt should be started (set when retrying; cleared when cron starts retry or run completes) */
  nextRetryAt?: Date;

  /** Lease while a worker is initiating the next retry SMS — prevents duplicate outbound attempts */
  retryInitiationLeaseUntil?: Date;
  retryInitiationLeaseOwner?: string;
  /** Log of each attempt for display and debugging */
  attempts?: HcHistoryAttempt[];
  /**
   * Every removal of an hc_active_calls row for this run must be preceded by a history write
   * (attempt push / completion / retry scheduling / stale-row cleanup). These entries record why.
   */
  activeCallRemovalAudit?: HcActiveCallRemovalAuditEntry[];
  /** True when call was answered by voicemail/machine (hung up, treated as not answered) */
  answeredByVoicemail?: boolean;
  errorMessage?: string; // Error details if call failed

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/** One attempt in a flow run (for step-by-step history) */
export interface HcHistoryAttempt {
  /** Start time: outbound SMS/voice placed at loved one (awaiting_reply), or legacy completion-only timestamp */
  at: Date;
  /** awaiting_reply = listen window open; finalized to voicemail | no_answer | answered when the attempt ends */
  result: 'voicemail' | 'no_answer' | 'answered' | 'awaiting_reply';
  duration?: number;
}

/** Written to HcHistory immediately before deleting hc_active_calls rows (audit / support). */
export type HcActiveCallRemovalReason =
  | 'removed_after_attempt_logged_and_retry_scheduled'
  | 'removed_after_attempt_logged_and_run_completed'
  | 'removed_after_new_history_created_for_legacy_active_call'
  | 'removed_stale_rows_before_initiating_scheduled_retry'
  /** Prefer orchestrated paths; kept for rare Twilio-SID-based cleanup */
  | 'removed_after_twilio_sid_resolution_request';

export interface HcActiveCallRemovalAuditEntry {
  at: Date;
  reason: HcActiveCallRemovalReason;
  removedActiveCallIds: string[];
}

export enum HcResponseType {
  TEXT_RESPONSE = 'HcResponseType.text_response',
  DTMF_INPUT = 'HcResponseType.dtmf_input',
  SMS_INPUT = 'HcResponseType.sms_input',
  AUDIO_FILE = 'HcResponseType.audio_file',
  TRANSCRIBED_AUDIO = 'HcResponseType.transcribed_audio',
  NO_RESPONSE = 'HcResponseType.no_response',
  CALL_FAILED = 'HcResponseType.call_failed'
}

export interface HcResponseData {
  // For text responses
  textMessage?: string;

  // For DTMF input
  dtmfSequence?: string;

  // For SMS input
  smsText?: string;

  // For audio files
  audioFileUrl?: string;
  audioFileSize?: number;
  audioDuration?: number; // in seconds

  // For transcribed audio
  transcriptionText?: string;
  transcriptionConfidence?: number; // 0-1 confidence score
  transcriptionLanguage?: string;
}

export interface HcNotification {
  type: HcNotificationType;
  recipientId: string; // ID of who was notified
  recipientType: HcNotificationRecipientType;
  sentAt: Date;
  method: HcNotificationMethod;
  status: HcNotificationStatus;
  errorMessage?: string;
}

export enum HcCallStatus {
  SCHEDULED = 'HcCallStatus.scheduled',
  INITIATED = 'HcCallStatus.initiated',
  IN_PROGRESS = 'HcCallStatus.in_progress',
  COMPLETED = 'HcCallStatus.completed',
  TIMEOUT = 'HcCallStatus.timeout',
  FAILED = 'HcCallStatus.failed',
  CANCELLED = 'HcCallStatus.cancelled'
}

export enum HcNotificationType {
  OK_RESPONSE = 'HcNotificationType.ok_response',
  NOT_OK_RESPONSE = 'HcNotificationType.not_ok_response',
  EMERGENCY_RESPONSE = 'HcNotificationType.emergency_response',
  NO_RESPONSE = 'HcNotificationType.no_response',
  CALL_FAILED = 'HcNotificationType.call_failed'
}

export enum HcNotificationRecipientType {
  CARE_SEEKER = 'HcNotificationRecipientType.care_seeker',
  EMERGENCY_CONTACT = 'HcNotificationRecipientType.emergency_contact',
  HEALTHCARE_PROVIDER = 'HcNotificationRecipientType.healthcare_provider',
  FAMILY_MEMBER = 'HcNotificationRecipientType.family_member'
}

export enum HcNotificationMethod {
  EMAIL = 'HcNotificationMethod.email',
  SMS = 'HcNotificationMethod.sms',
  PUSH_NOTIFICATION = 'HcNotificationMethod.push_notification',
  PHONE_CALL = 'HcNotificationMethod.phone_call'
}

export enum HcNotificationStatus {
  PENDING = 'HcNotificationStatus.pending',
  SENT = 'HcNotificationStatus.sent',
  DELIVERED = 'HcNotificationStatus.delivered',
  FAILED = 'HcNotificationStatus.failed',
  BOUNCED = 'HcNotificationStatus.bounced'
}

export interface HcNotificationAttempt {
  _id: string;
  historyId: string; // Reference to HcHistory._id - always stored as string
  contactId: string; // Reference to HcContact._id - always required
  readonly contact?: HcContact; // Populated on queries via aggregation
  communicationMethod: HcCommunicationMethodType;
  contactValue: string; // Phone number, email, etc.
  initiatedAt: Date;
  completedAt?: Date;
  status: HcNotificationAttemptStatus;
  responseData?: HcNotificationResponseData;
  retryAttempt: number; // Which retry attempt this was (0 = first attempt)
  errorMessage?: string;
  duration?: number; // Duration in seconds

  // Acknowledgment tracking (for NOT_OK and NEED_HELP scenarios)
  acknowledgmentRequired: boolean; // Whether this notification requires acknowledgment
  acknowledgmentReceivedAt?: Date; // When acknowledgment was received
  acknowledgmentContactId?: string; // Which contact acknowledged (for tracking in multi-contact scenarios)
  responseMeaning?: HcResponseMeaning; // The response meaning that triggered this notification
}

export enum HcNotificationAttemptStatus {
  INITIATED = 'HcNotificationAttemptStatus.initiated',
  IN_PROGRESS = 'HcNotificationAttemptStatus.in_progress',
  COMPLETED = 'HcNotificationAttemptStatus.completed',
  FAILED = 'HcNotificationAttemptStatus.failed',
  TIMEOUT = 'HcNotificationAttemptStatus.timeout',
  CANCELLED = 'HcNotificationAttemptStatus.cancelled'
}

export interface HcNotificationResponseData {
  // For voice calls
  callDuration?: number; // Duration in seconds
  dtmfResponse?: string; // DTMF input received
  audioResponseUrl?: string; // URL to recorded audio response
  transcriptionText?: string; // Transcribed audio response
  transcriptionConfidence?: number; // 0-1 confidence score

  // For SMS
  smsResponse?: string; // Text response received
  deliveryStatus?: string; // Delivery confirmation status

  // For Email
  emailOpened?: boolean; // Whether email was opened
  emailClicked?: boolean; // Whether links in email were clicked
  emailBounced?: boolean; // Whether email bounced
  bounceReason?: string; // Reason for bounce if applicable

  // Common fields
  responseReceivedAt?: Date; // When response was received
  responseType: HcNotificationResponseType;
}

export enum HcNotificationResponseType {
  VOICE_RESPONSE = 'HcNotificationResponseType.voice_response',
  DTMF_RESPONSE = 'HcNotificationResponseType.dtmf_response',
  SMS_RESPONSE = 'HcNotificationResponseType.sms_response',
  EMAIL_RESPONSE = 'HcNotificationResponseType.email_response',
  NO_RESPONSE = 'HcNotificationResponseType.no_response',
  DELIVERY_CONFIRMATION = 'HcNotificationResponseType.delivery_confirmation',
  BOUNCE_NOTIFICATION = 'HcNotificationResponseType.bounce_notification'
}

/**
 * Notification flow state machine states
 */
export enum HcNotificationFlowState {
  NOT_STARTED = 'HcNotificationFlowState.not_started',
  PENDING = 'HcNotificationFlowState.pending', // Notification flow initiated but not yet sent
  SENT = 'HcNotificationFlowState.sent', // Initial notifications sent
  WAITING_ACKNOWLEDGMENT = 'HcNotificationFlowState.waiting_acknowledgment', // Waiting for contact acknowledgment (NOT_OK, NEED_HELP)
  ACKNOWLEDGED = 'HcNotificationFlowState.acknowledged', // Contact acknowledged notification
  ESCALATING = 'HcNotificationFlowState.escalating', // Escalating to next contact or method
  DEADLINE_PASSED = 'HcNotificationFlowState.deadline_passed', // Acknowledgment deadline passed, notifications already sent
  COMPLETE = 'HcNotificationFlowState.complete', // Flow completed successfully
  FAILED = 'HcNotificationFlowState.failed' // Flow failed (all retries exhausted)
}
